import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Layout from "../../../Component/Layout";
import { PURCHASE_API } from "../../../apiConfig";


const fmtAmount = (val) =>
  val === null || val === undefined || val === ""
    ? "—"
    : Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (isoStr) => {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const dropdownOptionStyle = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

const filterCardStyle = { background: "#ffffff", borderRadius: 8, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 18 };
const filterLabelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#334155" };

const thStyle = {
  padding: "12px 14px",
  fontSize: "12px",
  fontWeight: "700",
  color: "#334155",
  textAlign: "left",
  whiteSpace: "nowrap",
  borderBottom: "2px solid #e2e8f0",
  background: "#f1f5f9",
  position: "sticky",
  top: 0,
};

const tdStyle = {
  padding: "11px 14px",
  fontSize: "13px",
  color: "#334155",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #f1f5f9",
};

// GST amount colors — customer (output GST) = red, supplier (input GST) = green
const CUSTOMER_GST_COLOR = "#b91c1c";
const SUPPLIER_GST_COLOR = "#047857";

function GSTReport() {
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    customers: [],
    suppliers: [],
  });

  const [filters, setFilters] = useState({
    location: "",
    customer: "",
    supplier: "",
    fromDate: "",
    toDate: "",
  });

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalCustomerGst: 0, totalSupplierGst: 0, netPayable: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load dropdown options once on mount — dedicated GST Report filters endpoint
  useEffect(() => {
    axios
      .get(`${PURCHASE_API}/gst-report/filters`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((err) => {
        console.error("GST REPORT FILTERS ERROR:", err);
      });
  }, []);

  const locationOptions = useMemo(
    () => (filterOptions.locations || []).map((loc) => ({ value: loc, label: loc })),
    [filterOptions.locations]
  );

  const customerOptions = useMemo(
    () =>
      (filterOptions.customers || []).map((c) => ({
        value: c.business_partner_name,
        label: c.business_partner_name,
      })),
    [filterOptions.customers]
  );

  const supplierOptions = useMemo(
    () =>
      (filterOptions.suppliers || []).map((s) => ({
        value: s.business_partner_name,
        label: s.business_partner_name,
      })),
    [filterOptions.suppliers]
  );

  const fetchReport = (activeFilters) => {
    setLoading(true);
    setError("");

    const params = {};
    if (activeFilters.location) params.location = activeFilters.location;
    if (activeFilters.customer) params.customer = activeFilters.customer;
    if (activeFilters.supplier) params.supplier = activeFilters.supplier;
    if (activeFilters.fromDate) params.fromDate = activeFilters.fromDate;
    if (activeFilters.toDate) params.toDate = activeFilters.toDate;

    axios
      .get(`${PURCHASE_API}/gst-report`, { params })
      .then((res) => {
        setRows(res.data?.rows || []);
        setSummary(res.data?.summary || { totalCustomerGst: 0, totalSupplierGst: 0, netPayable: 0 });
      })
      .catch((err) => {
        console.error("GST REPORT FETCH ERROR:", err);
        setError("Failed to load GST report. Please try again.");
        setRows([]);
        setSummary({ totalCustomerGst: 0, totalSupplierGst: 0, netPayable: 0 });
      })
      .finally(() => setLoading(false));
  };

  // Auto-fetch whenever any filter changes (debounced), including initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport(filters);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Derive the currency to display above the table (falls back to INR if rows have none yet)
  const displayCurrency = useMemo(() => {
    const withCurrency = rows.find((r) => r.currency);
    return withCurrency ? withCurrency.currency : "INR";
  }, [rows]);

  return (
    <Layout>
      <div style={{ padding: "30px 40px", backgroundColor: "#f8fafc", minHeight: "calc(100vh - 60px)" }}>

        {/* Page Header */}
        <div style={{ marginBottom: "28px" }}>
          <h2 style={{ margin: "0 0 4px 0", color: "#0f172a", fontWeight: "700", fontSize: "24px" }}>
            GST Report
          </h2>
          <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
            Customer GST (output) and Supplier GST (input) — customer rows in red, supplier rows in green
          </p>
        </div>

        {/* Filter Card */}
        <div style={filterCardStyle}>
          <div style={{ display: "flex", gap: "16px", flexWrap: "nowrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={filterLabelStyle}>Location</label>
              <Select
                options={locationOptions}
                value={locationOptions.find((o) => o.value === filters.location) || null}
                onChange={(opt) => handleChange("location", opt ? opt.value : "")}
                placeholder="Select"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={filterLabelStyle}>Customer</label>
              <Select
                options={customerOptions}
                value={customerOptions.find((o) => o.value === filters.customer) || null}
                onChange={(opt) => handleChange("customer", opt ? opt.value : "")}
                placeholder="Select"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={filterLabelStyle}>Supplier</label>
              <Select
                options={supplierOptions}
                value={supplierOptions.find((o) => o.value === filters.supplier) || null}
                onChange={(opt) => handleChange("supplier", opt ? opt.value : "")}
                placeholder="Select"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={filterLabelStyle}>From Date</label>
              <input
                type="date"
                className="form-control"
                style={{ height: 32, fontSize: "13px", width: "100%" }}
                value={filters.fromDate}
                onChange={(e) => handleChange("fromDate", e.target.value)}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={filterLabelStyle}>To Date</label>
              <input
                type="date"
                className="form-control"
                style={{ height: 32, fontSize: "13px", width: "100%" }}
                value={filters.toDate}
                onChange={(e) => handleChange("toDate", e.target.value)}
              />
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
            borderRadius: "10px", padding: "12px 16px", marginBottom: "18px", fontSize: "13px"
          }}>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "20px", marginBottom: "24px"
        }}>
          <div style={{
            background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
            border: "1px solid #fecaca", borderRadius: "18px", padding: "28px 30px",
            boxShadow: "0 4px 10px -2px rgba(239,68,68,0.15)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#b91c1c", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Customer GST
            </div>
            <div style={{ fontSize: "34px", fontWeight: "800", color: "#991b1b", marginTop: "8px" }}>
              ₹ {fmtAmount(summary.totalCustomerGst)}
            </div>
          </div>
          <div style={{
            background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
            border: "1px solid #a7f3d0", borderRadius: "18px", padding: "28px 30px",
            boxShadow: "0 4px 10px -2px rgba(16,185,129,0.15)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#047857", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Supplier GST
            </div>
            <div style={{ fontSize: "34px", fontWeight: "800", color: "#065f46", marginTop: "8px" }}>
              ₹ {fmtAmount(summary.totalSupplierGst)}
            </div>
          </div>
          <div style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
            border: "1px solid #bfdbfe", borderRadius: "18px", padding: "28px 30px",
            boxShadow: "0 4px 10px -2px rgba(59,130,246,0.15)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Net Payable
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
            </div>
            <div style={{
              fontSize: "34px", fontWeight: "800", marginTop: "8px",
              color: summary.netPayable < 0 ? "#047857" : "#1e3a8a"
            }}>
              {summary.netPayable < 0 ? "− " : ""}₹ {fmtAmount(Math.abs(summary.netPayable))}
            </div>
            {summary.netPayable < 0 && (
              <div style={{ fontSize: "12px", color: "#047857", marginTop: "4px", fontWeight: 600 }}>
              </div>
            )}
          </div>
        </div>

        {/* Report Table */}
        <div style={{
          background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", overflow: "hidden"
        }}>
          {/* Currency indicator — right aligned above table */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px",
            padding: "14px 18px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc"
          }}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Currency
            </span>
            <span style={{
              fontSize: "12px", fontWeight: "700", color: "#334155",
              background: "#e2e8f0", borderRadius: "6px", padding: "3px 10px"
            }}>
              {displayCurrency}
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Customer / Supplier Name</th>
                  <th style={thStyle}>PO Number</th>
                  <th style={thStyle}>Invoice Number</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>CGST</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>SGST</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>IGST</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Total GST</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} style={{ ...tdStyle, textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                      Loading GST report...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ ...tdStyle, textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                      No records found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const isCustomer = row.type === "customer";
                    const partyName = isCustomer ? row.customerName : row.supplierName;
                    // Customer rows -> billing id. Supplier rows -> invoice number.
                    const reference = isCustomer ? row.billingInvoice : row.supplierInvoice;

                    // Customer GST = red, Supplier GST = green
                    const gstColor = isCustomer ? CUSTOMER_GST_COLOR : SUPPLIER_GST_COLOR;
                    const totalGst = row.totalGst ?? (row.cgst + row.sgst + row.igst);

                    return (
                      <tr
                        key={`${row.type}-${row.billingInvoice || row.supplierInvoice || idx}-${idx}`}
                        style={{ background: isCustomer ? "#fef7f7" : "#f0fdf4" }}
                      >
                        <td style={{ ...tdStyle, fontWeight: "600" }}>{fmtDate(row.date)}</td>
                        <td style={tdStyle}>{partyName || ""}</td>
                        <td style={tdStyle}>{row.poNumber || ""}</td>
                        <td style={tdStyle}>{reference || ""}</td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: "600", color: gstColor }}>
                          {fmtAmount(row.cgst)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: "600", color: gstColor }}>
                          {fmtAmount(row.sgst)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: "600", color: gstColor }}>
                          {fmtAmount(row.igst)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: "700", color: gstColor }}>
                          {fmtAmount(totalGst)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default GSTReport;